import { Node, Edge } from 'reactflow';
import { LayoutDirection } from '../types';

/**
 * Legacy synchrone layout-algoritme — bewaard als fallback voor het geval
 * dat ELK.js faalt. Origineel uit OrganizationChart.tsx vóór de migratie
 * naar elkjs.
 *
 * Subtree-width-aware hierarchical layout met multi-row children. Centreert
 * elke parent boven zijn children. Wanneer een node meer dan
 * MAX_CHILDREN_PER_ROW children heeft worden ze in meerdere rijen gewrapt.
 * Externe shareholders worden bij hun verbonden entities geplaatst.
 */
export const legacyLayout = (
  nodes: Node[],
  edges: Edge[],
  direction: LayoutDirection = 'TB'
): { nodes: Node[]; edges: Edge[] } => {
  if (nodes.length === 0) return { nodes: [], edges };

  const isHorizontal = direction === 'LR' || direction === 'RL';

  // Node dimensions (must match CompanyNode.tsx rendering)
  const NODE_WIDTH = 220;
  const NODE_HEIGHT = 120;
  const NODE_GAP = 30;                // min gap between sibling nodes (cross-axis)
  const LEVEL_SPACING = 180;          // distance between levels (main-axis)
  const TREE_GAP = 120;              // gap between separate trees
  const MAX_CHILDREN_PER_ROW = 4;    // children beyond this wrap to next row
  const ROW_GAP = 40;                // vertical gap between rows in a multi-row group

  const crossSize = isHorizontal ? NODE_HEIGHT : NODE_WIDTH;
  const mainSize  = isHorizontal ? NODE_WIDTH  : NODE_HEIGHT;

  // ── Step 1: build adjacency from edges ──────────────────────────
  const primaryParentOf = new Map<string, string>();
  const childrenOf = new Map<string, string[]>();
  const nodeSet = new Set(nodes.map(n => n.id));

  nodes.forEach(n => childrenOf.set(n.id, []));

  // Group incoming edges per target, then pick the primary parent
  // (highest ownership %).
  const incomingEdges = new Map<string, Edge[]>();
  edges.forEach(edge => {
    if (!nodeSet.has(edge.source) || !nodeSet.has(edge.target)) return;
    if (!incomingEdges.has(edge.target)) incomingEdges.set(edge.target, []);
    incomingEdges.get(edge.target)!.push(edge);
  });

  incomingEdges.forEach((edgeList, targetId) => {
    let primaryEdge: Edge;
    if (edgeList.length === 1) {
      primaryEdge = edgeList[0];
    } else {
      const targetNode = nodes.find(n => n.id === targetId);
      const shareholders: Array<{name: string; percentage: number}> = targetNode?.data?.shareholders || [];
      let bestEdge = edgeList[0];
      let bestPct = -1;
      edgeList.forEach(e => {
        const sourceNode = nodes.find(n => n.id === e.source);
        const sourceName = sourceNode?.data?.name;
        const sh = shareholders.find((s: any) => s.name === sourceName);
        const pct = sh ? sh.percentage : 0;
        if (pct > bestPct) { bestPct = pct; bestEdge = e; }
      });
      primaryEdge = bestEdge;
    }
    primaryParentOf.set(targetId, primaryEdge.source);
    childrenOf.get(primaryEdge.source)!.push(targetId);
  });

  // ── Step 2: identify roots & separate external shareholders ─────
  const normalRootIds: string[] = [];
  const extRootIds: string[] = [];
  nodes.forEach(n => {
    if (!primaryParentOf.has(n.id)) {
      // External shareholders: country 'EXT' or id starting with 'ext-'
      if (n.data?.country === 'EXT' || n.id.startsWith('ext-')) {
        extRootIds.push(n.id);
      } else {
        normalRootIds.push(n.id);
      }
    }
  });

  // ── Step 3: partition children into rows & compute subtree dimensions
  const childRowsOf = new Map<string, string[][]>();
  const subtreeWidth = new Map<string, number>();
  const subtreeHeight = new Map<string, number>();

  const partitionIntoRows = (children: string[], max: number): string[][] => {
    const rows: string[][] = [];
    for (let i = 0; i < children.length; i += max) {
      rows.push(children.slice(i, i + max));
    }
    return rows;
  };

  const computeSubtreeDimensions = (id: string): void => {
    const children = childrenOf.get(id) || [];
    if (children.length === 0) {
      subtreeWidth.set(id, crossSize);
      subtreeHeight.set(id, mainSize);
      return;
    }

    children.forEach(ch => computeSubtreeDimensions(ch));

    let w: number;
    let h: number;

    if (children.length > MAX_CHILDREN_PER_ROW) {
      const rows = partitionIntoRows(children, MAX_CHILDREN_PER_ROW);
      childRowsOf.set(id, rows);

      const maxRowWidth = Math.max(...rows.map(row => {
        const rowW = row.reduce((sum, ch) => sum + (subtreeWidth.get(ch) || crossSize), 0)
          + NODE_GAP * (row.length - 1);
        return rowW;
      }));
      w = Math.max(crossSize, maxRowWidth);

      let rowStart = LEVEL_SPACING;
      for (let r = 1; r < rows.length; r++) {
        const prevRowMaxH = Math.max(
          ...rows[r - 1].map(ch => subtreeHeight.get(ch) || mainSize)
        );
        rowStart += prevRowMaxH + ROW_GAP;
      }
      const lastRowMaxH = Math.max(
        ...rows[rows.length - 1].map(ch => subtreeHeight.get(ch) || mainSize)
      );
      h = rowStart + lastRowMaxH;
    } else {
      const totalW = children.reduce(
        (sum, ch) => sum + (subtreeWidth.get(ch) || crossSize), 0
      ) + NODE_GAP * (children.length - 1);
      w = Math.max(crossSize, totalW);

      const maxChildH = Math.max(
        ...children.map(ch => subtreeHeight.get(ch) || mainSize)
      );
      h = LEVEL_SPACING + maxChildH;
    }

    subtreeWidth.set(id, w);
    subtreeHeight.set(id, h);
  };

  normalRootIds.forEach(r => computeSubtreeDimensions(r));
  extRootIds.forEach(r => computeSubtreeDimensions(r));

  // ── Step 4: position nodes recursively ──────────────────────────
  const positionMap = new Map<string, { x: number; y: number }>();

  const positionSubtree = (id: string, mainOffset: number, crossStart: number) => {
    const myWidth = subtreeWidth.get(id) || crossSize;
    const children = childrenOf.get(id) || [];

    const crossCenter = crossStart + myWidth / 2 - crossSize / 2;
    if (isHorizontal) {
      positionMap.set(id, { x: mainOffset, y: crossCenter });
    } else {
      positionMap.set(id, { x: crossCenter, y: mainOffset });
    }

    if (children.length === 0) return;

    const rows = childRowsOf.get(id) || [children];

    let rowMainOffset = mainOffset + LEVEL_SPACING;

    rows.forEach((row, rowIndex) => {
      if (rowIndex > 0) {
        const prevRow = rows[rowIndex - 1];
        const prevRowMaxH = Math.max(
          ...prevRow.map(ch => subtreeHeight.get(ch) || mainSize)
        );
        rowMainOffset += prevRowMaxH + ROW_GAP;
      }

      const rowTotalWidth = row.reduce(
        (s, ch) => s + (subtreeWidth.get(ch) || crossSize), 0
      ) + NODE_GAP * (row.length - 1);
      let childCross = crossStart + (myWidth - rowTotalWidth) / 2;

      row.forEach(ch => {
        const chWidth = subtreeWidth.get(ch) || crossSize;
        positionSubtree(ch, rowMainOffset, childCross);
        childCross += chWidth + NODE_GAP;
      });
    });
  };

  // ── Step 5: layout normal trees along the cross-axis ────────────
  let treeOffset = 0;
  const treeRoots = normalRootIds.map(id => ({
    id,
    size: subtreeWidth.get(id) || crossSize
  }));
  treeRoots.sort((a, b) => b.size - a.size);

  treeRoots.forEach(({ id }) => {
    positionSubtree(id, 0, treeOffset);
    treeOffset += (subtreeWidth.get(id) || crossSize) + TREE_GAP;
  });

  // ── Step 5b: position external shareholders near their targets ──
  extRootIds.forEach(extId => {
    const connectedTargets = edges
      .filter(e => e.source === extId && positionMap.has(e.target))
      .map(e => e.target);

    if (connectedTargets.length > 0) {
      let sumCross = 0;
      let minMain = Infinity;
      connectedTargets.forEach(tid => {
        const pos = positionMap.get(tid)!;
        if (isHorizontal) {
          sumCross += pos.y;
          minMain = Math.min(minMain, pos.x);
        } else {
          sumCross += pos.x;
          minMain = Math.min(minMain, pos.y);
        }
      });
      const avgCross = sumCross / connectedTargets.length;
      const extMain = minMain - LEVEL_SPACING;

      if (isHorizontal) {
        positionMap.set(extId, { x: extMain, y: avgCross });
      } else {
        positionMap.set(extId, { x: avgCross, y: extMain });
      }
    } else {
      if (isHorizontal) {
        positionMap.set(extId, { x: 0, y: treeOffset });
      } else {
        positionMap.set(extId, { x: treeOffset, y: 0 });
      }
      treeOffset += crossSize + TREE_GAP;
    }
  });

  // ── Step 6: handle direction flips (BT, RL) ────────────────────
  if (direction === 'BT' || direction === 'RL') {
    let maxMain = 0;
    positionMap.forEach(pos => {
      const v = isHorizontal ? pos.x : pos.y;
      if (v > maxMain) maxMain = v;
    });

    positionMap.forEach((pos, id) => {
      if (isHorizontal) {
        positionMap.set(id, { x: maxMain - pos.x, y: pos.y });
      } else {
        positionMap.set(id, { x: pos.x, y: maxMain - pos.y });
      }
    });
  }

  // ── Step 7: build final positioned node array ───────────────────
  const layoutedNodes = nodes.map(n => ({
    ...n,
    position: positionMap.get(n.id) || { x: 0, y: 0 },
  }));

  return { nodes: layoutedNodes, edges };
};
